import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription, switchMap, take } from 'rxjs';
import { MicrophoneService } from '../../../core/services/microphone.service';
import { MicrophoneState } from '../../../models/app/precall/microphone-state';
import { AudioInputDevice } from '../../../models/app/precall/audio-input-device';
import { MibIconsModule } from '@medinbox/angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-audio-level',
  imports: [MibIconsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './audio-level.component.html',
})
export class AudioLevelComponent implements OnInit, OnDestroy {
  private readonly microphone = inject(MicrophoneService);
  private readonly destroyRef = inject(DestroyRef);

  audioLevelSubscription: Subscription | undefined;
  state = signal<MicrophoneState | undefined>(undefined);
  devices = signal<AudioInputDevice[]>([]);
  current = computed(
    () =>
      this.devices().find((device) => device.deviceId === this.state()?.activeDeviceId) ||
      undefined,
  );

  ngOnInit(): void {
    this.audioLevelSubscription = this.microphone.store.state$.subscribe(
      this.onStateHandler.bind(this),
    );

    // Subscribe to store devices (updated whenever refreshDevices() is called anywhere)
    this.microphone.store.devices$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((devices) => {
        this.devices.set(devices);
        if (devices.length > 0) {
          const defaultDevice = devices.find((device) => device.deviceId === 'default');
          if (defaultDevice && !this.state()?.activeDeviceId) {
            this.changeDevice(defaultDevice);
          }
        }
      });

    // Trigger initial enumeration
    this.microphone.refreshDevices().subscribe();
  }

  ngOnDestroy(): void {
    this.audioLevelSubscription?.unsubscribe();
  }

  protected onStateHandler(state: MicrophoneState): void {
    this.state.set(state);
  }

  protected onChangeHandler(event: Event): void {
    const device = (event.target as any).value as AudioInputDevice | undefined;

    if (!device) {
      console.warn('No device selected');
      return;
    }
    this.changeDevice(device);
  }

  changeDevice(device: AudioInputDevice): void {
    this.audioLevelSubscription?.unsubscribe();

    this.audioLevelSubscription = this.microphone
      .switchDevice$(device.deviceId)
      .pipe(
        take(1),
        switchMap(() => this.microphone.store.state$),
      )
      .subscribe(this.onStateHandler.bind(this));
  }
}
